import { useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { MenuItem, InputLabel, FormControl, Input, Grid, Select, InputAdornment } from "@material-ui/core";
import getList from "../../../lib/api/getList";
import NumberFormat from "react-number-format";
import { TemporaryCredentials } from "aws-sdk";

/**
 * @COMPONENT_NAME FormItemUnitConversion
 * @DESCRIPTION 자재 단위 변환 기능을 제공하는 폼 아이템 컴포넌트
 *              자재 코드와 버전을 기반으로 해당 자재의 단위 목록을 가져와서
 *              변환 전 수량 입력, 단위 선택, 변환 후 수량 표시의 3개 영역으로 구성
 *              변환 전 수량과 선택된 단위의 변환 계수를 곱하여 기준 단위 수량을 계산
 *
 * @PROPS
 *   - {string} id - 컴포넌트 ID
 *   - {string} className - CSS 클래스명
 *   - {Object} column - 컬럼 설정 정보 (required, thousandSeparator, allowNegative, numberPrefix 등)
 *   - {string} label - 라벨 텍스트
 *   - {Object} InputLabelProps - InputLabel 컴포넌트에 전달할 props
 *   - {number} value - 현재 값 (변환 후 수량)
 *   - {Function} onChange - 값 변경 시 호출되는 콜백 함수
 *   - {Object} focusRef - 포커스 참조 객체
 *   - {Object} formData - 폼 데이터 (materialCode, materialCodeVer 포함)
 *
 * @STATE
 *   - {Array} selectItems - 선택 가능한 단위 목록 (value, name, conversionFactor, isBaseUnit)
 *   - {Object} selectedItem - 현재 선택된 단위 정보
 *   - {string} baseMaterialUnit - 기준 단위 심볼
 *   - {number} beforeConversionNumber - 변환 전 수량
 *   - {number} afterConversionNumber - 변환 후 수량 (기준 단위 기준)
 *   - {number} conversionFactor - 현재 선택된 단위의 변환 계수
 *
 * @CALLBACKS
 *   - {Function} handleBeforeConversionNumberChange - 변환 전 수량 입력 시 호출되는 콜백
 *   - {Function} handleSelectChange - 단위 선택 변경 시 호출되는 콜백
 *
 * @EXAMPLE
 *   <FormItemUnitConversion
 *     id="quantity"
 *     label="수량"
 *     value={formData.quantity}
 *     onChange={handleQuantityChange}
 *     formData={{ materialCode: 'M001', materialCodeVer: 1 }}
 *   />
 */
const FormItemUnitConversion = (props) => {
    const { t } = useTranslation();
    const { id, className, column, label, InputLabelProps, value, onChange, focusRef, formData, ...others } = props;

    const [selectItems, setSelectItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [baseMaterialUnit, setBaseMaterialUnit] = useState("");
    const [beforeConversionNumber, setBeforeConversionNumber] = useState(0);
    const [afterConversionNumber, setAfterConversionNumber] = useState(0);
    const [conversionFactor, setConversionFactor] = useState(1);

    const { materialCode, materialCodeVer } = formData || {};
    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                if (mounted && (!materialCode || !materialCodeVer)) {
                    setSelectItems([]);
                    setSelectedItem(null);
                    setBaseMaterialUnit("");
                    return;
                }

                const response = await getList(`MaterialUnitGroupDetail/${materialCode}/${materialCodeVer}/unitList`);
                const { data } = response;

                if (mounted) {
                    if (data && data.length > 0) {
                        const items = data.map((item) => ({
                            value: item.unitId,
                            name: item.unitSymbol,
                            conversionFactor: item.conversionFactor,
                            isBaseUnit: item.unitId === item.baseUnitId,
                        }));

                        setSelectItems(items);

                        const baseUnit = items.find((item) => item.isBaseUnit);
                        if (baseUnit) {
                            setBaseMaterialUnit(baseUnit.name);
                        }

                        if (items.length > 0) {
                            setSelectedItem(items[0]);
                            setConversionFactor(items[0].conversionFactor);
                        }
                    } else {
                        setSelectItems([]);
                        setSelectedItem(null);
                        setBaseMaterialUnit("");
                    }
                }
            } catch (error) {
                console.error("Failed to fetch unit list:", error);
                if (mounted) {
                    setSelectItems([]);
                    setSelectedItem(null);
                    setBaseMaterialUnit("");
                }
            }
        })();

        return () => {
            mounted = false;
        };
    }, [materialCode, materialCodeVer]);

    useEffect(() => {
        setAfterConversionNumber(beforeConversionNumber * conversionFactor);
    }, [beforeConversionNumber, conversionFactor]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            onChange(afterConversionNumber);
        }, 10);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [afterConversionNumber]);

    const handleBeforeConversionNumberChange = (event) => {
        const number = parseFloat(event.target.value) || 0;
        setBeforeConversionNumber(number);
    };

    const handleSelectChange = (event) => {
        const unitData = selectItems.find((item) => item.value === event.target.value);
        if (unitData) {
            setSelectedItem(unitData);
            setConversionFactor(unitData.conversionFactor);
        }
    };

    const beforeConversionNumberProps = {
        id: id + "-beforeConversionNumber",
        value: beforeConversionNumber,
        onChange: handleBeforeConversionNumberChange,
        inputComponent: NumberFormatCustom,
        inputRef: focusRef,
        ...others,
    };

    const selectProps = {
        id: id + "-select",
        value: selectedItem?.value || "",
        onChange: handleSelectChange,
    };

    // const afterConversionNumberProps = {
    //     id: id + "-afterConversionNumber",
    //     value: afterConversionNumber,
    //     endAdornment: baseMaterialUnit ? <InputAdornment position="end">{baseMaterialUnit}</InputAdornment> : null,
    //     inputComponent: NumberFormatCustom,
    // };

    return (
        <div style={{ width: "45%", height: "100%", margin: "1%" }}>
            <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel {...InputLabelProps} shrink={true}>
                            {label}
                        </InputLabel>
                        <Input {...beforeConversionNumberProps} />
                    </FormControl>
                </Grid>

                <Grid item xs={6}>
                    <FormControl fullWidth>
                        <InputLabel shrink={true} {...InputLabelProps}></InputLabel>
                        <Select {...selectProps} displayEmpty>
                            {selectItems.length === 0 ? (
                                <MenuItem value="" disabled>
                                    {t("term.none")}
                                </MenuItem>
                            ) : (
                                selectItems.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                {/* 
                <Grid item xs={4}>
                    <FormControl disabled fullWidth>
                        <InputLabel id={id} shrink={true} {...InputLabelProps}></InputLabel>
                        <Input {...afterConversionNumberProps} />
                    </FormControl>
                </Grid> */}
            </Grid>
        </div>
    );
};

/**
 * @COMPONENT_NAME NumberFormatCustom
 * @DESCRIPTION react-number-format을 사용한 커스텀 숫자 입력 컴포넌트
 *              천단위 구분 기호, 음수 허용 여부, 접두사 등을 설정 가능
 *
 * @PROPS
 *   - {Object} inputRef - 입력 요소 참조 객체
 *   - {Object} column - 컬럼 설정 정보 (thousandSeparator, allowNegative, numberPrefix)
 *   - {Function} onChange - 값 변경 시 호출되는 콜백 함수
 *
 * @CALLBACKS
 *   - {Function} onChangeValue - NumberFormat 값 변경 시 호출되는 콜백
 */
function NumberFormatCustom(props) {
    const { inputRef, column, onChange, ...other } = props;

    const onChangeValue = (value, sourceInfo) => {
        if (onChange) {
            if (sourceInfo?.source === "event") {
                let event = cloneDeep(sourceInfo.event);

                event.target.value = value.floatValue;

                onChange(event);
            }
        }
    };
    const thousandSeparator = column
        ? column.thousandSeparator !== undefined
            ? column.thousandSeparator
            : true
        : true;
    const allowNegative = column ? (column.allowNegative !== undefined ? column.allowNegative : true) : true;

    return (
        <NumberFormat
            {...other}
            prefix={column?.numberPrefix}
            getInputRef={inputRef}
            onValueChange={onChangeValue}
            thousandSeparator={thousandSeparator}
            allowNegative={allowNegative}
        />
    );
}

export default FormItemUnitConversion;
